import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import { z } from "zod";
import { useStore } from "@/lib/store-context";
import { ProductCard } from "@/components/store/product-card";
import { Container, meta } from "@/components/store/page-parts";

const searchSchema = z.object({ q: z.string().optional().catch(undefined) });

export const Route = createFileRoute("/products/")({
  validateSearch: searchSchema,
  head: () => meta("All Digital Products — betteraccess.store", "Browse verified subscriptions, software, gaming codes and digital access."),
  component: Products,
});

function Products() {
  const { products, categories } = useStore();
  const { q } = Route.useSearch();
  const [term, setTerm] = useState(q ?? "");
  useEffect(() => setTerm(q ?? ""), [q]);
  const [cat, setCat] = useState("all");
  const [sort, setSort] = useState("featured");

  const list = useMemo(
    () =>
      products
        .filter(p => (cat === "all" || p.categorySlug === cat) && `${p.name} ${p.description}`.toLowerCase().includes(term.toLowerCase()))
        .sort((a, b) =>
          sort === "low"
            ? (a.variants[0]?.price ?? 0) - (b.variants[0]?.price ?? 0)
            : sort === "rating"
            ? b.rating - a.rating
            : 0
        ),
    [products, term, cat, sort]
  );

  return (
    <Container className="py-8 sm:py-10">
      <div className="max-w-2xl">
        <p className="text-xs font-extrabold uppercase text-primary">Verified catalog</p>
        <h1 className="mt-2 text-3xl font-black sm:text-4xl">All digital products</h1>
        <p className="mt-3 text-muted-foreground">Clear plans, honest delivery windows and support for every order.</p>
      </div>
      <div className="mt-8 flex flex-col gap-3 border-y py-4 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 size-4 text-muted-foreground" />
          <input
            value={term}
            onChange={e => setTerm(e.target.value)}
            placeholder="Search the catalog"
            className="h-10 w-full rounded-md border bg-background pl-10 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto">
          {[{ slug: "all", name: "All" }, ...categories].map(c => (
            <button
              key={c.slug}
              onClick={() => setCat(c.slug)}
              className={`whitespace-nowrap rounded-md border px-3 py-2 text-xs font-bold ${
                cat === c.slug ? "border-primary bg-primary text-primary-foreground" : "bg-background"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-xs font-bold lg:shrink-0">
          <SlidersHorizontal className="size-4" />
          <select value={sort} onChange={e => setSort(e.target.value)} className="h-10 rounded-md border bg-background px-3">
            <option value="featured">Featured</option>
            <option value="low">Price: low</option>
            <option value="rating">Top rated</option>
          </select>
        </label>
      </div>
      <p className="my-5 text-xs font-semibold text-muted-foreground">{list.length} products found</p>
      {list.length ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4 lg:gap-5">
          {list.map(p => (
            <ProductCard key={p.slug} product={p} />
          ))}
        </div>
      ) : (
        <div className="py-24 text-center">
          <h2 className="text-xl font-bold">No products found</h2>
          <p className="mt-2 text-sm text-muted-foreground">Try a different search or category.</p>
        </div>
      )}
    </Container>
  );
}
